import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Send, ExternalLink } from 'lucide-react';

const Github = ({ size = 24, ...props }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <path d="M15 22v-4a4.8 4.8 0 0 0-1-3.5c3 0 6-2 6-5.5.08-1.25-.27-2.48-1-3.5.28-1.15.28-2.35 0-3.5 0 0-1 0-3 1.5-2.64-.5-5.36-.5-8 0C6 2 5 2 5 2c-.3 1.15-.3 2.35 0 3.5A5.403 5.403 0 0 0 4 9c0 3.5 3 5.5 6 5.5-.39.49-.68 1.05-.85 1.65-.17.6-.22 1.23-.15 1.85v4" />
    <path d="M9 18c-4.51 2-5-2-7-2" />
  </svg>
);

const Twitter = ({ size = 24, ...props }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <path d="M22 4s-.7 2.1-2 3.4c1.6 10-9.4 17.3-18 11.6 2.2.1 4.4-.6 6-2C3 15.5.5 9.6 3 5c2.2 2.6 5.6 4.1 9 4-.9-4.2 4-6.6 7-3.8 1.1 0 3-1.2 3-1.2z" />
  </svg>
);

const Linkedin = ({ size = 24, ...props }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z" />
    <rect x="2" y="9" width="4" height="12" />
    <circle cx="4" cy="4" r="2" />
  </svg>
);

const Instagram = ({ size = 24, ...props }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
    <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
    <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
  </svg>
);

const footerData = [
  {
    title: 'Company',
    links: [
      { label: 'About us', href: '/about' },
      { label: "We're hiring", href: '/careers', badge: 'Join us' },
      { label: 'Hire interns for your company', href: '/hire-interns' },
      { label: 'Post a Job', href: '/post-job' },
      { label: 'Competitions', href: '/competitions' },
    ],
  },
  {
    title: 'Resources',
    links: [
      { label: 'Team Diary', href: '/team-diary' },
      { label: 'Blog', href: '/blog' },
      { label: 'Our Services', href: '/services' },
      { label: 'Free Job Alerts', href: '/job-alerts' },
    ],
  },
  {
    title: 'Legal',
    links: [
      { label: 'Terms & Conditions', href: '/terms' },
      { label: 'Privacy', href: '/privacy' },
      { label: 'Contact us', href: '/contact' },
      { label: 'Annual Returns', href: '/annual-returns' },
      { label: 'Grievance Redressal', href: '/grievance' },
      { label: 'Resume Maker', href: '/resume', highlighted: true },
    ],
  },
  {
    title: 'Explore',
    links: [
      { label: 'Sitemap', href: '/sitemap' },
      { label: 'College TPO registration', href: '/tpo-registration' },
      { label: 'List of Companies', href: '/companies' },
      { label: 'Jobs for Women', href: '/jobs/women', badge: 'Special' },
    ],
  },
];

const socialLinks = [
  { icon: Linkedin, href: 'https://linkedin.com', label: 'LinkedIn', color: 'hover:text-[#0077b5]' },
  { icon: Twitter, href: 'https://twitter.com', label: 'Twitter', color: 'hover:text-[#1da1f2]' },
  { icon: Github, href: 'https://github.com', label: 'GitHub', color: 'hover:text-[#24292e]' },
  { icon: Instagram, href: 'https://instagram.com', label: 'Instagram', color: 'hover:text-[#e1306c]' },
  { icon: Send, href: 'https://telegram.org', label: 'Telegram', color: 'hover:text-[#0088cc]' },
];

export default function Footer() {
  const containerVariants = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.08,
        delayChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        type: 'spring',
        stiffness: 100,
        damping: 15,
      },
    },
  };

  return (
    <footer className="relative overflow-hidden bg-gradient-to-b from-[#18181b] to-[#09090b] text-zinc-400 border-t border-zinc-800/80 pt-20 pb-12 z-10">
      {/* Premium background glowing animations */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden select-none">
        <div className="absolute top-1/4 left-1/4 -translate-x-1/2 -translate-y-1/2 w-[350px] h-[350px] bg-indigo-500/10 rounded-full blur-[80px] animate-glow-slow" />
        <div className="absolute top-1/3 right-1/4 translate-x-1/2 -translate-y-1/2 w-[300px] h-[300px] bg-purple-500/10 rounded-full blur-[70px] animate-pulse-slow" style={{ animationDelay: '2s' }} />
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-20">
        
        {/* Top interactive grid layout */}
        <motion.div 
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8 pb-16"
          variants={containerVariants}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-100px" }}
        >
          {footerData.map((column, idx) => (
            <motion.div 
              key={idx} 
              variants={itemVariants}
              className="flex flex-col space-y-4"
            >
              <h3 className="text-sm font-semibold tracking-wider text-zinc-100 uppercase">
                {column.title}
              </h3>
              <ul className="space-y-3">
                {column.links.map((link, lIdx) => (
                  <li key={lIdx}>
                    <Link
                      to={link.href}
                      className={`group relative inline-flex items-center text-[15px] font-medium transition-all duration-300 hover:text-white py-0.5 ${
                        link.highlighted 
                          ? 'text-indigo-400 font-semibold' 
                          : 'text-zinc-400'
                      }`}
                    >
                      {/* Underline expanding from left on hover */}
                      <span className="relative z-10 flex items-center gap-1.5 transition-transform duration-300 group-hover:translate-x-1.5">
                        {link.label}
                        {link.highlighted && <ExternalLink size={12} className="opacity-70 group-hover:opacity-100 transition-opacity" />}
                      </span>
                      
                      {/* Hover slide underline */}
                      <span className="absolute bottom-0 left-0 w-0 h-[1.5px] bg-indigo-500 transition-all duration-300 group-hover:w-full" />
                      
                      {/* Micro Pill Badges */}
                      {link.badge && (
                        <span className="ml-2.5 inline-flex items-center rounded-full bg-zinc-800/80 px-2 py-0.5 text-[10px] font-medium text-indigo-300 ring-1 ring-inset ring-indigo-500/30 backdrop-blur-sm group-hover:bg-indigo-950/50 group-hover:text-indigo-200 transition-colors">
                          {link.badge}
                        </span>
                      )}
                    </Link>
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </motion.div>

        {/* Divider above bottom footer */}
        <div className="w-full h-px bg-gradient-to-r from-transparent via-zinc-800 to-transparent my-8" />

        {/* Bottom Footer Section */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-8 pt-4">
          
          {/* Brand Identity & Tagline */}
          <div className="flex flex-col space-y-3">
            <div className="flex items-center space-x-3 cursor-pointer group">
              {/* Airplane Logo reproduction from Navbar */}
              <div className="relative w-12 h-9 flex items-center justify-center">
                <svg width="45" height="30" viewBox="0 0 70 45" fill="none" xmlns="http://www.w3.org/2000/svg" className="transform transition-transform duration-500 group-hover:rotate-12 group-hover:scale-110">
                  <path d="M5 35 Q 15 35, 25 25" stroke="#888888" strokeWidth="1.5" strokeDasharray="3 3" fill="none" />
                  <path d="M25 25 L65 5 L45 35 L38 27 L33 34 L30 26 Z" fill="#6366f1" />
                  <path d="M65 5 L38 27 L30 26 Z" fill="#818cf8" />
                  <path d="M38 27 L33 34 L30 26 Z" fill="#4f46e5" />
                </svg>
              </div>
              <div className="font-bold tracking-tight text-lg">
                <span className="text-indigo-400">INTERNS</span>
                <span className="text-white ml-0.5">BRIDGE</span>
              </div>
            </div>
            <p className="text-[13px] text-zinc-500 italic font-medium">
              “Built for the next generation careers”
            </p>
          </div>

          {/* Copyright text */}
          <div className="text-xs text-zinc-500 md:text-center order-3 md:order-2">
            &copy; {new Date().getFullYear()} InternsBridge. All rights reserved. Made with love for creators.
          </div>

          {/* Social Icons with Glow Effects */}
          <div className="flex items-center space-x-4 order-2 md:order-3">
            {socialLinks.map((social, index) => {
              const Icon = social.icon;
              return (
                <a
                  key={index}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={social.label}
                  className={`relative p-2.5 rounded-full bg-zinc-900/60 border border-zinc-800 text-zinc-400 transition-all duration-300 ${social.color} hover:bg-zinc-800/80 hover:border-zinc-700/60 group hover:-translate-y-1 hover:shadow-[0_0_15px_rgba(99,102,241,0.25)]`}
                >
                  <Icon size={18} className="transition-transform duration-300 group-hover:scale-110" />
                </a>
              );
            })}
          </div>

        </div>

      </div>
    </footer>
  );
}
